import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { ParallaxImage } from "../components/ParallaxImage";
import { IMAGES } from "../data/images";
const PANELS = [{
  step: "01",
  title: "MCC",
  copy: "A focused Grade 10–12 environment in Lekki, taught by Canadian-trained, well-seasoned instructors.",
  image: IMAGES.campus
}, {
  step: "02",
  title: "OSSD",
  copy: "The Ontario Secondary School Diploma, a credit-based credential that approaches learning holistically.",
  image: IMAGES.learning
}, {
  step: "03",
  title: "University",
  copy: "A diploma accepted by universities across Canada, the USA, the UK and Australia.",
  image: IMAGES.library
}];
const INDEX = [{
  label: "Curriculum & Courses",
  copy: "The University Pathway Program, subject by subject.",
  path: "/academics/curriculum"
}, {
  label: "University Pathways",
  copy: "How the OSSD leads to global study.",
  path: "/academics/pathways"
}, {
  label: "Student Life",
  copy: "Sport, debate, enterprise, arts and service.",
  path: "/student-life"
}];
function HorizontalJourney() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start start", "end end"]
  });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", `-${100 * (PANELS.length - 1) / PANELS.length}%`]);
  if (reduce) {
    return <section className="bg-[#061f38] px-5 py-24 text-white md:px-9" data-id="element-493" data-mp="43izspl5d4aq">
        {PANELS.map(p => <div key={p.title} className="border-t border-white/20 py-10" data-id="element-494" data-mp="86fdq2zqsc8c">
            <p className="text-xs text-[#f0b1b9]" data-id="element-495" data-mp="d4qrupx2l8wd">{p.step}</p>
            <h3 className="mt-4 text-6xl font-medium tracking-[-0.07em]" data-id="element-496" data-mp="i1z8gm2kl4vi">{p.title}</h3>
            <p className="mt-4 max-w-md text-white/70" data-id="element-497" data-mp="2e5n8grtfwx6w">{p.copy}</p>
          </div>)}
      </section>;
  }
  return <section ref={ref} style={{
    height: `${PANELS.length * 100}vh`
  }} className="relative bg-[#061f38] text-white" data-id="element-498" data-mp="297bz0i724op7">
      <div className="sticky top-0 h-screen overflow-hidden" data-id="element-499" data-mp="2nw8cwvvsovxq">
        <p className="absolute left-5 top-28 z-10 text-xs font-semibold tracking-[0.18em] text-[#f0b1b9] md:left-9" data-id="element-500" data-mp="2j4874mt1n8t8">THE ACADEMIC JOURNEY · SCROLL</p>
        <motion.div style={{
        x,
        width: `${PANELS.length * 100}%`
      }} className="flex h-full" data-id="element-501" data-mp="2wubn4w1by6a8">
          {PANELS.map(p => <div key={p.title} style={{
          width: `${100 / PANELS.length}%`
        }} className="grid h-full items-center gap-8 px-5 pb-20 pt-40 md:px-9 lg:grid-cols-12 lg:pt-24" data-id="element-502" data-mp="2sww5vn3l3mul">
              <div className="lg:col-span-5" data-id="element-503" data-mp="31w10iic0yw1h">
                <p className="text-sm text-[#f0b1b9]" data-id="element-504" data-mp="1cg1o8sd40qhm">{p.step}</p>
                <h3 className="mt-4 text-[clamp(4rem,11vw,10rem)] font-medium leading-[0.85] tracking-[-0.08em]" data-id="element-505" data-mp="1cie1is7xacoc">{p.title}</h3>
                <p className="mt-6 max-w-sm text-lg leading-8 text-white/70" data-id="element-506" data-mp="3j4tublnw6wni">{p.copy}</p>
              </div>
              <div className="relative hidden h-[60vh] overflow-hidden lg:col-span-6 lg:col-start-7 lg:block" data-id="element-507" data-mp="3o46ge46xvzp1">
                <img src={p.image} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-508" data-mp="39fim018txw0o" />
              </div>
            </div>)}
        </motion.div>
        <div className="absolute bottom-10 left-5 right-5 h-px bg-white/20 md:left-9 md:right-9" data-id="element-509" data-mp="30dsu6ub1cv2d">
          <motion.div style={{
          scaleX: scrollYProgress
        }} className="h-full origin-left bg-[#c5233d]" data-id="element-510" data-mp="35df7f3qv6bb6" />
        </div>
      </div>
    </section>;
}
export function Academics() {
  return <>
      <PageHero variant="split" eyebrow="ACADEMICS" title="Prepared for the work that comes next." description="A curriculum completely oriented toward college and university-level coursework." image={IMAGES.learning} imageAlt="Teacher and students working together" breadcrumb="Academics" data-id="element-511" data-mp="2ve6aq34m6il4" />

      <main data-id="element-512" data-mp="2pr0vxntblxpk">
        <HorizontalJourney data-id="element-513" data-mp="2ppptpsfiwy0k" />

        <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-28 md:px-9 lg:grid-cols-12 lg:py-40" data-id="element-514" data-mp="2ppy3vck9hp82">
          <div className="lg:col-span-5" data-id="element-515" data-mp="2ln1mfp6gjz5p">
            <ParallaxImage src={IMAGES.lab} alt="Students conducting a chemistry experiment" className="aspect-[4/5]" reveal="left" data-id="element-516" data-mp="2gp9tcru2u6zx" />
          </div>
          <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7" data-id="element-517" data-mp="2bt2l2se7kjet">
            <Reveal data-id="element-518" data-mp="26t6fiopv4jx5">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-519" data-mp="26tpzsc2zjkec">EVERY STUDENT, GUIDED</p>
              <h2 className="mt-6 text-5xl font-medium leading-[0.92] tracking-[-0.07em] md:text-6xl" data-id="element-520" data-mp="1xzl1x8pfp8pb">A plan for each student, reviewed every semester.</h2>
            </Reveal>
            <div className="mt-12 divide-y divide-[#dedbd3] border-y border-[#dedbd3]" data-id="element-521" data-mp="1sydsl57n81gj">
              {[["Individual Growth Plan", "Personal goals in academics, leadership and career building, set with an adviser and assessed each semester."], ["Guidance counselling", "Integrated support to keep each student on track for their university ambitions."], ["Canadian-certified instruction", "Dedicated teaching and mentorship across a wide range of courses over two semesters."]].map(([title, copy], i) => <Reveal key={title} delay={i * 0.08} className="py-6" data-id="element-522" data-mp="1nubpu8amgkhm" data-mpi={i}>
                  <h3 className="text-xl font-medium tracking-[-0.03em]" data-id="element-523" data-mp="q8e8ie7zal79">{title}</h3>
                  <p className="mt-2 max-w-lg text-sm leading-6 text-[#4f5963]" data-id="element-524" data-mp="v6pjwtdx93rl">{copy}</p>
                </Reveal>)}
            </div>
          </div>
        </section>

        <section className="border-t border-[#dedbd3] bg-[#f2f0eb] py-20 md:py-28" data-id="element-525" data-mp="1047jht61u6mx">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-526" data-mp="14yvapzsftf57">
            <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-527" data-mp="7gbn6pke6gqo">GO DEEPER</p>
            <div className="mt-8 border-t border-[#061f38]" data-id="element-528" data-mp="cffzvq6iywlr">
              {INDEX.map((item, i) => <Reveal key={item.path} delay={i * 0.06} data-id="element-529" data-mp="4eqifodlcitk" data-mpi={i}>
                  <Link to={item.path} className="group grid items-center gap-3 border-b border-[#cfcac0] py-8 md:grid-cols-[1fr_1fr_auto]" data-id="element-530" data-mp="3vm31ya1y58cn">
                    <span className="text-3xl font-medium tracking-[-0.05em] transition-colors duration-200 group-hover:text-[#bb1e35] md:text-5xl" data-id="element-531" data-mp="e4a04qpki6mu">{item.label}</span>
                    <span className="text-sm text-[#4f5963]" data-id="element-532" data-mp="3hls6etpoz5l5">{item.copy}</span>
                    <ArrowRightIcon className="text-[#bb1e35] transition-transform duration-200 ease-out group-hover:translate-x-1.5" data-id="element-533" data-mp="3cn8iwovg9lqo" />
                  </Link>
                </Reveal>)}
            </div>
          </div>
        </section>
      </main>

      <CTABand title="Let’s map the right academic route for your child." image={IMAGES.library} data-id="element-534" data-mp="3mjtpvfr156x5" />
    </>;
}